import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getProfilePhotoUrl, getGalleryPhotoUrl } from '../utils/imageUtils';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Breadcrumbs,
  Divider,
  Chip,
  Avatar,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Skeleton,
  Dialog,
  DialogContent,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  PhotoCamera as PhotoCameraIcon,
  Person as PersonIcon,
  Photo as PhotoIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import PhotoUploadDialog from '../components/gallery/PhotoUploadDialog';
import EditGalleryDialog from '../components/gallery/EditGalleryDialog';
import DeleteConfirmationDialog from '../components/common/DeleteConfirmationDialog';

const GalleryPage = () => {
  const { t } = useTranslation();
  const { galleryId } = useParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [openPhotoDialog, setOpenPhotoDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const isOwner = gallery && currentUser && gallery.user._id === currentUser._id;

  // Fetch gallery and photos
  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/galleries/${galleryId}`);
        setGallery(response.data.data);
        setPhotos(response.data.data.photos || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching gallery:', err);
        setError(err.response?.data?.error || 'Failed to load gallery');
      } finally {
        setIsLoading(false);
      }
    };

    fetchGallery();
  }, [galleryId]);

  const handleOpenMenu = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const handlePhotoClick = (photo) => {
    setSelectedPhoto(photo);
  };

  const handleClosePhotoView = () => {
    setSelectedPhoto(null);
  };

  const handleAddPhoto = (photoData) => {
    // PhotoUploadDialog already handles the API call
    // This function just updates the UI with the returned data
    try {
      // photoData is the response from the successful upload
      const uploadedPhotos = Array.isArray(photoData) ? photoData : [photoData];
      
      setPhotos(prev => [...uploadedPhotos, ...prev]);
      setOpenPhotoDialog(false);
      
      // Update gallery photo count and cover photo if needed
      const newPhotoCount = (gallery.photoCount || 0) + uploadedPhotos.length;
      
      setGallery(prev => ({
        ...prev,
        photoCount: newPhotoCount,
        // Set first uploaded photo as cover if gallery has no cover
        ...((!prev.coverPhoto && uploadedPhotos.length > 0) && {
          coverPhoto: uploadedPhotos[0]
        })
      }));
      
      setSnackbar({
        open: true,
        message: `${uploadedPhotos.length} photo(s) added successfully`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating UI after photo upload:', err);
      setSnackbar({
        open: true,
        message: 'Photos uploaded but failed to update display',
        severity: 'warning'
      });
    }
  };

  const handleUpdateGallery = async (galleryData) => {
    try {
      const response = await api.put(`/galleries/${gallery._id}`, galleryData);
      setGallery(prev => ({
        ...prev,
        ...response.data.data
      }));
      setOpenEditDialog(false);
      setSnackbar({
        open: true,
        message: 'Gallery updated successfully',
        severity: 'success'
      });
    } catch (err) {
      console.error('Error updating gallery:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to update gallery',
        severity: 'error'
      });
    }
  };

  const handleDeleteGallery = async () => {
    try {
      await api.delete(`/galleries/${gallery._id}`);
      setOpenDeleteDialog(false);
      setSnackbar({
        open: true,
        message: 'Gallery deleted successfully',
        severity: 'success'
      });
      
      // Navigate back to profile after deletion
      setTimeout(() => {
        navigate(`/profile/${currentUser.username}`);
      }, 1500);
    } catch (err) {
      console.error('Error deleting gallery:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || 'Failed to delete gallery',
        severity: 'error'
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 2 }}>
          <Skeleton variant="text" width="40%" height={40} />
          <Skeleton variant="text" width="20%" height={30} />
          <Skeleton variant="rectangular" width="100%" height={300} sx={{ mt: 2 }} />
        </Paper>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate(-1)}
            sx={{ mt: 2 }}
          >
            Go Back
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        {/* Breadcrumbs navigation */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link 
            to="/"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            Home
          </Link>
          <Link 
            to={`/profile/${gallery.user.username}`}
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            {gallery.user.firstName}'s Profile
          </Link>
          <Typography color="text.primary">Gallery</Typography>
        </Breadcrumbs>
        
        {/* Gallery header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {gallery.name}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Chip
                icon={gallery.isPublic ? <PublicIcon /> : <LockIcon />}
                label={gallery.isPublic ? t('gallery.publicLabel', { ns: 'gallery' }) : t('gallery.privateLabel', { ns: 'gallery' })}
                color={gallery.isPublic ? "primary" : "default"}
                size="small"
                sx={{ mr: 2 }}
              />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Avatar 
                  src={getProfilePhotoUrl(gallery.user.profilePhotoThumbnail)} 
                  alt={gallery.user.firstName}
                  sx={{ width: 24, height: 24, mr: 1 }}
                >
                  <PersonIcon fontSize="small" />
                </Avatar>
                <Typography variant="body2">
                  <Link 
                    to={`/profile/${gallery.user.username}`}
                    style={{ textDecoration: 'none', color: 'inherit', fontWeight: 500 }}
                  >
                    {gallery.user.firstName} {gallery.user.lastName}
                  </Link>
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {formatDistanceToNow(new Date(gallery.createdAt), { addSuffix: true })}
              </Typography>
            </Box>
            
            {gallery.description && (
              <Typography variant="body1" paragraph>
                {gallery.description}
              </Typography>
            )}
          </Box>
          
          <Box>
            {isOwner && (
              <>
                <IconButton
                  aria-label={t('gallery.galleryOptions', { ns: 'gallery' })}
                  aria-controls="gallery-menu"
                  aria-haspopup="true"
                  onClick={handleOpenMenu}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id="gallery-menu"
                  anchorEl={menuAnchorEl}
                  keepMounted
                  open={Boolean(menuAnchorEl)}
                  onClose={handleCloseMenu}
                >
                  <MenuItem onClick={() => {
                    handleCloseMenu();
                    setOpenEditDialog(true);
                  }}>
                    <ListItemIcon>
                      <EditIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('gallery.editAction', { ns: 'gallery' })} />
                  </MenuItem>
                  <MenuItem onClick={() => {
                    handleCloseMenu();
                    setOpenDeleteDialog(true);
                  }}>
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={t('gallery.deleteAction', { ns: 'gallery' })} />
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        {/* Photos section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Photos ({gallery.photoCount || 0})
          </Typography>
          
          {isOwner && (
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={() => setOpenPhotoDialog(true)}
            >
              Add Photos
            </Button>
          )}
        </Box>
        
        {photos.length > 0 ? (
          <ImageList 
            variant="masonry" 
            cols={3} 
            gap={8}
            sx={{ mt: 1 }}
          >
            {photos.map((photo) => (
              <ImageListItem 
                key={photo._id}
                onClick={() => handlePhotoClick(photo)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.9,
                    transform: 'scale(1.02)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <img
                  src={getGalleryPhotoUrl(photo.imageUrl)}
                  alt={photo.title || 'Gallery photo'}
                  loading="lazy"
                />
                {(photo.title || !photo.isPublic) && (
                  <ImageListItemBar
                    title={photo.title}
                    actionIcon={
                      !photo.isPublic && (
                        <IconButton sx={{ color: 'rgba(255, 255, 255, 0.54)' }}>
                          <LockIcon />
                        </IconButton>
                      )
                    }
                  />
                )}
              </ImageListItem>
            ))}
          </ImageList>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <PhotoIcon sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.5 }} />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              {isOwner
                ? t('gallery.empty.ownerMessage', { ns: 'gallery' })
                : t('gallery.empty.visitorMessage', { ns: 'gallery' })}
            </Typography>
            {isOwner && (
              <Button 
                variant="contained" 
                startIcon={<PhotoCameraIcon />}
                onClick={() => setOpenPhotoDialog(true)}
                sx={{ mt: 2 }}
              >
                {t('gallery.addFirstPhoto', { ns: 'gallery' })}
              </Button>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Photo view dialog */}
      <Dialog
        open={!!selectedPhoto}
        onClose={handleClosePhotoView}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          {selectedPhoto && (
            <>
              <img
                src={getGalleryPhotoUrl(selectedPhoto.imageUrl)}
                alt={selectedPhoto.title || 'Gallery photo'}
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
              {(selectedPhoto.title || selectedPhoto.description) && (
                <Box sx={{ p: 2, backgroundColor: 'rgba(0, 0, 0, 0.7)', color: 'white', position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                  {selectedPhoto.title && (
                    <Typography variant="h6">{selectedPhoto.title}</Typography>
                  )}
                  {selectedPhoto.description && (
                    <Typography variant="body2">{selectedPhoto.description}</Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Dialogs */}
      {isOwner && (
        <>
          <PhotoUploadDialog 
            open={openPhotoDialog}
            onClose={() => setOpenPhotoDialog(false)}
            onSave={handleAddPhoto}
            galleryId={galleryId}
          />
          
          <EditGalleryDialog 
            open={openEditDialog}
            onClose={() => setOpenEditDialog(false)}
            onSave={handleUpdateGallery}
            gallery={gallery}
          />
          
          <DeleteConfirmationDialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
            onConfirm={handleDeleteGallery}
            title={t('gallery.deleteTitle', { ns: 'gallery' })}
            content={t('gallery.deleteContent', { ns: 'gallery' })}
            confirmButtonText={t('gallery.deleteConfirmButton', { ns: 'gallery' })}
          />
        </>
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GalleryPage;
